import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";

type Row = {
  id: string;
  template_key: string;
  channel: string;
  status: "pending" | "sent" | "failed" | "skipped";
  error_message: string | null;
  to_phone_e164: string;
  created_at: string;
};

type WindowKey = "24h" | "7d" | "30d";
const WINDOWS: Record<WindowKey, number> = { "24h": 1, "7d": 7, "30d": 30 };

const STATUS_TONE: Record<Row["status"], string> = {
  sent: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30",
  failed: "bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/30",
  skipped: "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30",
  pending: "bg-muted text-muted-foreground border-border",
};

/** Group long Twilio errors into a stable bucket for charting. */
function bucketError(msg: string | null): string {
  if (!msg) return "unknown";
  if (msg.startsWith("no_phone_on_file")) return "no_phone_on_file";
  if (msg.startsWith("no_channel_opt_in")) return "no_channel_opt_in";
  const m = msg.match(/Twilio (\d{3})/);
  if (m) return `twilio_${m[1]}`;
  if (/timeout/i.test(msg)) return "network_timeout";
  if (/fetch/i.test(msg)) return "network_error";
  return msg.slice(0, 60);
}

/** CSV-escape a single field per RFC 4180. */
function csvField(v: unknown): string {
  const s = v === null || v === undefined ? "" : String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function exportCsv(rows: Row[], windowKey: WindowKey) {
  const header = ["template", "status", "reason", "phone", "timestamp"];
  const lines = [header.join(",")];
  for (const r of rows) {
    const reason = r.status === "failed" || r.status === "skipped" ? bucketError(r.error_message) : "";
    lines.push([
      csvField(r.template_key),
      csvField(r.status),
      csvField(reason),
      csvField(r.to_phone_e164),
      csvField(r.created_at),
    ].join(","));
  }
  const blob = new Blob(["\uFEFF" + lines.join("\r\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `sms-outbox-${windowKey}-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const SmsOutboxPanel = () => {
  const [windowKey, setWindowKey] = useState<WindowKey>("24h");
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async (w: WindowKey) => {
    setLoading(true);
    const since = new Date(Date.now() - WINDOWS[w] * 86400_000).toISOString();
    const { data, error } = await supabase
      .from("sms_outbox")
      .select("id, template_key, channel, status, error_message, to_phone_e164, created_at")
      .gte("created_at", since)
      .order("created_at", { ascending: false })
      .limit(1000);
    if (!error && data) setRows(data as Row[]);
    setLoading(false);
  };

  useEffect(() => { load(windowKey); }, [windowKey]);

  // Realtime: prepend any new sms_outbox row that falls inside the active window.
  useEffect(() => {
    const sinceMs = Date.now() - WINDOWS[windowKey] * 86400_000;
    const channel = supabase
      .channel(`sms_outbox_${windowKey}`)
      .on(
        "postgres_changes",
        { event: "INSERT", schema: "public", table: "sms_outbox" },
        (payload) => {
          const row = payload.new as Row;
          if (!row?.created_at || new Date(row.created_at).getTime() < sinceMs) return;
          setRows((prev) => (prev.some((r) => r.id === row.id) ? prev : [row, ...prev].slice(0, 1000)));
        },
      )
      .on(
        "postgres_changes",
        { event: "UPDATE", schema: "public", table: "sms_outbox" },
        (payload) => {
          const row = payload.new as Row;
          setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, ...row } : r)));
        },
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [windowKey]);

  const totals = useMemo(() => {
    const t = { sent: 0, failed: 0, skipped: 0, pending: 0 };
    rows.forEach((r) => { t[r.status] += 1; });
    return t;
  }, [rows]);

  const successRate = useMemo(() => {
    const denom = totals.sent + totals.failed;
    if (denom === 0) return null;
    return Math.round((totals.sent / denom) * 1000) / 10;
  }, [totals]);

  /** Per-template breakdown: counts + grouped error reasons. */
  const byTemplate = useMemo(() => {
    const map = new Map<string, {
      sent: number; failed: number; skipped: number; pending: number;
      errors: Map<string, number>;
    }>();
    for (const r of rows) {
      const e = map.get(r.template_key) ?? {
        sent: 0, failed: 0, skipped: 0, pending: 0, errors: new Map<string, number>(),
      };
      e[r.status] += 1;
      if (r.status === "failed" || r.status === "skipped") {
        const b = bucketError(r.error_message);
        e.errors.set(b, (e.errors.get(b) ?? 0) + 1);
      }
      map.set(r.template_key, e);
    }
    return Array.from(map.entries())
      .map(([template_key, v]) => ({
        template_key,
        ...v,
        total: v.sent + v.failed + v.skipped + v.pending,
        errors: Array.from(v.errors.entries())
          .map(([reason, count]) => ({ reason, count }))
          .sort((a, b) => b.count - a.count),
      }))
      .sort((a, b) => b.total - a.total);
  }, [rows]);

  const recentFailures = useMemo(
    () => rows.filter((r) => r.status === "failed" || r.status === "skipped").slice(0, 25),
    [rows],
  );

  return (
    <div className="space-y-6">
      {/* Window selector */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-base font-medium">SMS outbox</h3>
          <p className="text-sm text-muted-foreground">
            Twilio dispatch results across SMS / WhatsApp / RCS. Last {WINDOWS[windowKey]}d, max 1k rows.
          </p>
        </div>
        <div className="flex gap-1">
          {(Object.keys(WINDOWS) as WindowKey[]).map((w) => (
            <Button
              key={w}
              size="sm"
              variant={windowKey === w ? "default" : "outline"}
              onClick={() => setWindowKey(w)}
            >
              {w}
            </Button>
          ))}
          <Button size="sm" variant="ghost" onClick={() => load(windowKey)} disabled={loading}>
            {loading ? "…" : "Refresh"}
          </Button>
          <Button size="sm" variant="outline" onClick={() => exportCsv(rows, windowKey)} disabled={rows.length === 0}>
            Export CSV
          </Button>
        </div>
      </div>

      {/* Top-line counters */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Card className="p-4">
          <div className="text-xs text-muted-foreground">Total</div>
          <div className="text-2xl font-semibold mt-1">{rows.length}</div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-muted-foreground">Sent</div>
          <div className="text-2xl font-semibold mt-1 text-emerald-600 dark:text-emerald-400">
            {totals.sent}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-muted-foreground">Failed</div>
          <div className="text-2xl font-semibold mt-1 text-red-600 dark:text-red-400">
            {totals.failed}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-muted-foreground">Skipped</div>
          <div className="text-2xl font-semibold mt-1 text-amber-600 dark:text-amber-400">
            {totals.skipped}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-xs text-muted-foreground">Success rate</div>
          <div className="text-2xl font-semibold mt-1">
            {successRate === null ? "—" : `${successRate}%`}
          </div>
        </Card>
      </div>

      {/* Per-template breakdown */}
      <section className="space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground">By template</h4>
        {loading && rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : byTemplate.length === 0 ? (
          <p className="text-sm text-muted-foreground">No SMS activity in this window.</p>
        ) : (
          <ul className="space-y-2">
            {byTemplate.map((t) => (
              <li key={t.template_key}>
                <Card className="p-4 space-y-3">
                  <div className="flex items-baseline justify-between gap-3 flex-wrap">
                    <div className="font-mono text-sm">{t.template_key}</div>
                    <div className="text-xs text-muted-foreground">
                      {t.total} total · <span className="text-emerald-600 dark:text-emerald-400">{t.sent} sent</span>
                      {t.failed > 0 && <> · <span className="text-red-600 dark:text-red-400">{t.failed} failed</span></>}
                      {t.skipped > 0 && <> · <span className="text-amber-600 dark:text-amber-400">{t.skipped} skipped</span></>}
                    </div>
                  </div>
                  {t.errors.length > 0 && (
                    <div className="space-y-1">
                      <div className="text-xs uppercase tracking-wide text-muted-foreground">
                        Reasons
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {t.errors.map((e) => (
                          <Badge key={e.reason} variant="outline" className="font-mono text-xs">
                            {e.reason} · {e.count}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Most recent failures / skips */}
      <section className="space-y-3">
        <h4 className="text-sm font-medium text-muted-foreground">
          Recent failures &amp; skips
        </h4>
        {recentFailures.length === 0 ? (
          <p className="text-sm text-muted-foreground">None — clean window.</p>
        ) : (
          <ul className="space-y-2">
            {recentFailures.map((r) => (
              <li key={r.id}>
                <Card className="p-3 flex items-start justify-between gap-3">
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className={STATUS_TONE[r.status]}>
                        {r.status}
                      </Badge>
                      <span className="font-mono text-xs">{r.template_key}</span>
                      <span className="text-xs text-muted-foreground">via {r.channel}</span>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {r.error_message ?? "—"}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-xs text-muted-foreground">
                      {new Date(r.created_at).toLocaleString()}
                    </div>
                  </div>
                </Card>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};

export default SmsOutboxPanel;
